<template lang="pug">
  SupplierLayout
    CommonSection.b-supplier-chats-page(title="Чаты")
      SpecChatScreen(
        :chats="chats"
        :activeChat="activeChat"
        :messages="messages"
        :canCreate="false"
        :canWrite="canWrite"
        itemsAreLinks
        requestClickable
        @select="openChat"
        @send="send"
        @back="closeChat"
        @goto-item="gotoItem"
        @goto-request="gotoRequest"
        )
</template>

<script>
import SupplierLayout from '@/layouts/Supplier.vue';
import CommonSection from '@/components/CommonSection.vue';
import SpecChatScreen from '@/components/spec-chat/SpecChatScreen.vue';
import { apiRun } from '@/model/apiRun';
import model from '@/model/supplierCabinet';
import specChat from '@/model/specChat';

/**
 * Чаты поставщика (ТЗ 6.10, `/supplier/chats`): список чатов по всем спецификациям, где у него есть
 * запросы; кнопок создания нет — трёхсторонние чаты создаёт дизайнер. Экран — тот же общий
 * компонент ленты, что у дизайнера; различия роли переданы свойствами (ТЗ 12.6).
 *
 * Позиции и запрос в системном событии кликабельны и ведут в карточку запроса (ТЗ 6.10).
 * В архивном проекте поле ввода скрыто по признаку «только чтение» из ответа сервера (3.5).
 * Сгенерировано Claude.
 */
export default {
  name: 'SupplierChatsPage',
  components: { SupplierLayout, CommonSection, SpecChatScreen },
  data() {
    return {
      chats: [],
      activeChat: null,
      messages: [],
    };
  },
  computed: {
    routeChatId() { return Number(this.$route.params.chatId) || null; },
    canWrite() { return !this.activeChat?.readOnly; },
  },
  watch: {
    routeChatId(id) {
      if (id && this.activeChat?.id !== id) this.selectById(id);
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.chats = await model.fetchChats();
      if (this.routeChatId) this.selectById(this.routeChatId);
    },
    selectById(id) {
      const chat = this.chats.find((c) => c.id === id);
      if (chat) this.openChat(chat, false);
    },
    async openChat(chat, navigate = true) {
      this.activeChat = chat;
      this.messages = await specChat.fetchMessages(chat.id);
      if (navigate && this.routeChatId !== chat.id) {
        this.$router.replace({ name: 'supplier-chat', params: { chatId: String(chat.id) } });
      }
    },
    /** Возврат к списку на узких экранах: список и лента показываются поочерёдно (ТЗ 6.10). */
    closeChat() {
      this.activeChat = null;
      this.messages = [];
      if (this.routeChatId) this.$router.replace({ name: 'supplier-chats' });
    },
    async send({ text, files }) {
      if (!this.activeChat) return;
      const sent = await apiRun(
        () => specChat.sendMessage(this.activeChat.id, text, files),
        'Не удалось отправить сообщение',
      );
      if (!sent) return;
      this.messages = await specChat.fetchMessages(this.activeChat.id);
      this.chats = await model.fetchChats(false);
    },
    /** Переход к запросу из системного события: событие несёт его идентификатор (ТЗ 6.10, 8.3). */
    gotoRequest(request) {
      const id = request?.id ?? null;
      this.$router.push(id
        ? { name: 'supplier-request', params: { requestId: String(id) } }
        : { name: 'supplier-requests' });
    },
    /**
     * Единый переход «к позиции» у поставщика (ТЗ 6.10, 6.8): карточка запроса открывается с
     * параметром позиции — она подсвечивается и показывается. Запрос берётся из того самого
     * события, по которому щёлкнули: ссылка на запрос — часть данных события (ТЗ 8.3), поэтому
     * угадывать по ленте не нужно.
     *
     * Событие без ссылки на запрос (например, в трёхстороннем чате) запрос не называет, а в данных
     * чата его нет вовсе: связи «чат → запрос» в ответах сервера не существует. В этом случае
     * переход ведёт в список запросов, а не в наугад выбранный запрос.
     */
    gotoItem(item, message) {
      const requestId = message?.eventData?.request?.id ?? null;
      this.$router.push(requestId
        ? {
          name: 'supplier-request',
          params: { requestId: String(requestId) },
          query: item?.id ? { item: String(item.id) } : {},
        }
        : { name: 'supplier-requests' });
    },
  },
};
</script>

<style scoped lang="scss">
.supplier-chats-page {
  padding-bottom: 40px;
}
</style>
